import { Component, computed, signal } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { ControlConfig } from '../../../models/api/reports/report';
import { ControlValueHandler } from '../../../utilities/control-value-handler';
import { PickListComponent } from '../../core/controls/pick-list.component';
import { SelectFieldComponent } from '../../core/fields/select-field.component';
import { PageHeaderComponent } from '../../core/labels/page-header.component';
import { BaseCriteriaComponent } from './base-criteria.component';

interface PaycodeCriteriaFormControls {
  payCodesSource?: FormControl<any[] | null>;
  payCodes?: FormControl<any[] | null>;
  payCodeSeries?: FormControl<string[] | null>;
}

@Component({
  selector: 'app-paycode-criteria',
  imports: [SelectFieldComponent, PageHeaderComponent, PickListComponent],
  template: `
    <app-page-header label="Pay code criteria" headerSize="h3" />
    @if (showPayCodes()) {
      <app-pick-list
        [sourceControl]="fg.controls.payCodesSource"
        [targetControl]="fg.controls.payCodes"
        labelName="name"
        keyName="code"
        [showFilter]="true"
        [showTargetFilter]="true"
        [showSort]="true"
        [showMove]="true"
        [showAddRemoveAll]="true"
        [disabled]="isPayCodesDisabled()"
        [width]="850"
        (targetChanged)="onPayCodesChanged($event)"
      />
    }
    @if (showPayCodeSeries()) {
      <div class="tw-mt-6">
        <app-select-field
          [label]="controlConfig.getControlLabel('PAYCODE_SERIES')"
          optionLabel="name"
          optionValue="code"
          [virtualScroll]="false"
          [showHeader]="true"
          [multiple]="true"
          [showClear]="true"
          [maxSelectedLabels]="3"
          [options]="getPayCodeSeriesOptions()"
          [control]="fg.controls.payCodeSeries"
          valueClasses="tw-max-w-[400px]"
          [disabled]="isPayCodeSeriesDisabled()"
        />
      </div>
    }
  `,
})
export class PaycodeCriteriaComponent extends BaseCriteriaComponent {
  // Visibility signals
  showPayCodes = signal(false);
  showPayCodeSeries = signal(false);

  // Signals for tracking form control values (for mutual exclusion)
  payCodesValue = signal<string[] | null>(null);
  payCodeSeriesValue = signal<string[] | null>(null);

  // Computed for mutual exclusion - disable one if the other has values
  isPayCodesDisabled = computed(() => {
    const seriesValues = this.payCodeSeriesValue();
    return seriesValues !== null && seriesValues.length > 0;
  });

  isPayCodeSeriesDisabled = computed(() => {
    const codesValues = this.payCodesValue();
    return codesValues !== null && codesValues.length > 0;
  });

  // Form controls
  fg = new FormGroup<PaycodeCriteriaFormControls>({
    // Controls are created dynamically by control-configuration.service
  });

  protected readonly regionName = 'Pay Code Criteria';

  protected CONTROL_CONFIGS: ControlConfig[] = [
    {
      erfNames: 'PAYCODE_LOB',
      formControlName: 'payCodes',
      visibility: this.showPayCodes,
      type: 'multiselect',
    },
    {
      erfNames: 'PAYCODE_SERIES',
      formControlName: 'payCodeSeries',
      visibility: this.showPayCodeSeries,
      type: 'multiselect',
    },
  ];

  override ngOnInit(): void {
    super.ngOnInit();

    // Set initial values
    this.payCodesValue.set(this.fg.get('payCodes')?.value);
    this.payCodeSeriesValue.set(this.fg.get('payCodeSeries')?.value);
  }

  protected override loadControlConfiguration(config: ControlConfig): void {
    super.loadControlConfiguration(config);

    // Special handling for PAYCODE_LOB - set both source and target controls
    if (config.formControlName === 'payCodes' && config.controlRef) {
      const control = config.controlRef;

      // Ensure source control exists
      const sourceControl = this.ensureFormControl<any[]>('payCodesSource', []);

      // Always set the source control with all available options
      sourceControl.setValue(control.options || []);

      // Set the target control with selected values (as objects, not codes)
      const userValue = control.defaultValue;
      if (userValue && userValue.trim().length > 0) {
        const codes = ControlValueHandler.parseMultiSelectValue(
          userValue,
          control.displayType === 'CHECKBOXLIST' ||
            control.displayType === 'MULTISELECTLISTBOX',
        );
        const selectedItems =
          control.options?.filter((opt) =>
            Array.isArray(codes)
              ? codes.includes(opt.code)
              : codes === opt.code,
          ) || [];
        this.fg.get('payCodes')?.setValue(selectedItems);
      } else {
        this.fg.get('payCodes')?.setValue([]);
      }
    }
  }

  protected override setupFormListeners(): void {
    // Subscribe to form control changes to update signals for mutual exclusion
    // When pay codes are selected, disable pay code series (and vice versa)
    this.dispose.subs = this.fg
      .get('payCodes')
      ?.valueChanges.subscribe((value) => {
        this.payCodesValue.set(value);

        // Disable pay code series FormControl if pay codes are selected
        const seriesControl = this.fg.get('payCodeSeries');
        if (value && value.length > 0) {
          seriesControl?.disable({ emitEvent: false });
        } else {
          seriesControl?.enable({ emitEvent: false });
        }
      });

    this.dispose.subs = this.fg
      .get('payCodeSeries')
      ?.valueChanges.subscribe((value) => {
        this.payCodeSeriesValue.set(value);

        // Disable pay codes FormControls if pay code series are selected
        const sourceControl = this.fg.get('payCodesSource');
        const targetControl = this.fg.get('payCodes');
        if (value && value.length > 0) {
          sourceControl?.disable({ emitEvent: false });
          targetControl?.disable({ emitEvent: false });
        } else {
          sourceControl?.enable({ emitEvent: false });
          targetControl?.enable({ emitEvent: false });
        }
      });
  }

  protected override transformValueForSave(
    config: ControlConfig,
    value: any,
  ): any {
    // Special handling for PAYCODE_LOB - extract codes from objects
    if (config.controlRef?.erfName === 'PAYCODE_LOB' && Array.isArray(value)) {
      return value.map((item) => item.code);
    }
    return value;
  }

  getPayCodeSeriesOptions() {
    return this.controlConfig.getControlOptions('PAYCODE_SERIES');
  }

  onPayCodesChanged(items: any[]): void {
    // Update signal and disable/enable series control based on selection
    this.payCodesValue.set(items);
    const seriesControl = this.fg.get('payCodeSeries');
    if (items && items.length > 0) {
      seriesControl?.disable({ emitEvent: false });
    } else {
      seriesControl?.enable({ emitEvent: false });
    }
  }
}
